/**
 * In this tracking context, a product has five kinds of identifiers
 * 1. big commerce ID: this is what's used in add to cart operation, also serving
 * as the parent product id that encapsulate a series of variants
 * 2. variant id: the big commerce id but for the specific variant
 * 3. sku id: 9 digits GERS sku id
 * 4. legacy id: when you search for the product in bigC console, it displays a
 * sku, this is the legacy id
 * 5. PIM id: this is what you see in the name and urls of the parent product
 */
import { useEffect, useMemo, useState } from 'react'
import { ProductType } from '@/types'
import { BigCommerceCart } from '@/types/Cart'
import { SHOP_ORIGIN } from '@/services/Configuration'


const coalesceToString = (value: string | number) => {
  if (typeof value === 'string') {
    return value
  }
  if (typeof value === 'number') {
    if (Number.isInteger(value)) {
      return value.toFixed()
    }
    return value.toFixed(2)
  }
  return `${value || ''}`
}

type setAddToCartDataType = (v: {
  quantity: number,
  attribute: { [key: number]: number },
}) => void
type UseWunderkindTrackingInputType = {
  product: ProductType,
  cart: BigCommerceCart | null,
  sku: string,
  calculatedPrice: string,
  variantImage: string,
  selectedVariantId: number,
  isUserSignedIn: boolean
}
type AddToCartFormDataType = {
  action: 'add',
  'qty[]': string,
  product_id: string, // the parent product's big commerce id
  attribute: { [key: string]: string }
}
type UseWunderkindTrackingType = (p: UseWunderkindTrackingInputType) => { setAddToCartData: setAddToCartDataType }
const useWunderkindTracking: UseWunderkindTrackingType = ({
  product,
  cart,
  sku,
  calculatedPrice,
  variantImage,
  selectedVariantId,
  isUserSignedIn = false
}) => {
  const allCartItems = Object.values(cart?.lineItems ?? {}).reduce(
    (allItems, oneTypeOfItems) => [...allItems, ...oneTypeOfItems],
    [],
  )
  const cartQty = allCartItems.reduce((total, item) => total + item?.quantity ?? 0, 0)


  const [addToCartData, setAddToCartData] = useState<{ quantity: number, attribute: { [key: number]: number } }>({
    quantity: 0,
    attribute: {}
  })
  // parse into the api schema: /remote/v1/cart/add
  const addToCartFormData = useMemo<AddToCartFormDataType>(() => {
    let addToCartFormData: AddToCartFormDataType = {
      action: 'add',
      'qty[]': coalesceToString(addToCartData.quantity),
      product_id: coalesceToString(product.id), // the parent product's big commerce id
      attribute: {}
    }
    if (Object.entries(addToCartData.attribute ?? {}).length) {
      const [index, value] = Object.entries(addToCartData.attribute)[0]
      addToCartFormData.attribute[index] = coalesceToString(value)
    }
    return addToCartFormData
  }, [addToCartData, product.id])


  const selectedVariant = product.variants.find(v => v.id === selectedVariantId)

  useEffect(() => {
    if (selectedVariantId && sku && product) {
      window['wunderkind'] = {
        sku_id: selectedVariant?.sku ? product.warranty.variants?.[selectedVariant.sku]?.legacy_variant_id : product.sku,
        productSkuId: coalesceToString(selectedVariant?.sku ? product.warranty.variants?.[selectedVariant.sku]?.legacy_variant_id : product.sku),

        product_id: product.warranty.additional.legacy_product_id || product.sku,
        itemId: coalesceToString(product.warranty.additional.legacy_product_id || product.sku),
        productName: product?.name,
        imageUrl: variantImage,
        price: calculatedPrice,
        categoryIds: product?.categories,
        categoryInfo: product?.category_info,
        cartQty: coalesceToString(cartQty),
        cartValue: parseFloat(`${cart?.cartAmount || 0}`).toFixed(2),
        addToCartFormData,
        isUserSignedIn,
        itemUrl: `${SHOP_ORIGIN}${product.custom_url.url}?sku=${sku}`,
        addToCartEvent: 'addToCartEvent',

        lowestPrice: calculatedPrice?.match(/[0-9]+\.[0-9]{2}/g)?.[0] ?? calculatedPrice,

      }
    }
  }, [
    selectedVariant,
    product,
    variantImage,
    calculatedPrice,
    cartQty,
    cart?.cartAmount,
    addToCartFormData,
    isUserSignedIn,
    product.custom_url.url,
  ])

  return {
    setAddToCartData,
  }
}

export default useWunderkindTracking
